import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  useDevice, useUpdateDevice, useDeleteDevice, useSetDeviceStatus,
  useReprovisionDevice, useFleets, useDeviceBackends, useDeviceJobs,
  useSSHSessions, usePlaybooks, useTwin, usePatchDesired, useRunPlaybook,
} from '@/api/devices'
import { useDeviceTypes } from '@/api/admin'
import { usePackages, useAssignDeviceOTA } from '@/api/packages'
import { useDashboards, useDashboard, useCreatePanel, useUpdatePanel, useDeletePanel } from '@/api/telemetry'
import { RequirePermission } from '@/components/RequirePermission'
import { useRoles } from '@/hooks/usePermission'
import WebSSHModal from '@/components/WebSSHModal'
import MetricsPanel from '@/components/MetricsPanel'
import type { Device, Package, Panel, PanelType } from '@/types'

interface Props {
  tenantId: string | null
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  width: '100%',
}

const sectionStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius)',
  padding: 20,
  marginBottom: 16,
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 4,
  display: 'block',
}

const RESERVED_TAG_KEYS = new Set(['ansible_host', 'ansible_user'])

function TagEditor({ tags, onChange }: { tags: Record<string, unknown>; onChange: (t: Record<string, unknown>) => void }) {
  const customTags = Object.entries(tags).filter(([k]) => !RESERVED_TAG_KEYS.has(k))
  const [newKey, setNewKey] = useState('')
  const [newVal, setNewVal] = useState('')

  function removeTag(key: string) {
    const next = { ...tags }
    delete next[key]
    onChange(next)
  }

  function addTag(e: React.FormEvent) {
    e.preventDefault()
    if (!newKey.trim()) return
    onChange({ ...tags, [newKey.trim()]: newVal.trim() })
    setNewKey('')
    setNewVal('')
  }

  return (
    <div>
      <div style={{ ...labelStyle, marginBottom: 8 }}>Custom tags</div>
      {customTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {customTags.map(([k, v]) => (
            <span key={k} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
              borderRadius: 4, padding: '3px 8px', fontSize: 12, fontFamily: 'var(--font-mono)',
            }}>
              <span style={{ color: 'var(--color-text-muted)' }}>{k}</span>
              <span style={{ color: 'var(--color-border)' }}>=</span>
              <span>{String(v)}</span>
              <button
                type="button"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 14, padding: 0, lineHeight: 1, marginLeft: 2 }}
                onClick={() => removeTag(k)}
              >×</button>
            </span>
          ))}
        </div>
      )}
      <form onSubmit={addTag} style={{ display: 'flex', gap: 6 }}>
        <input style={{ ...inputStyle, width: 120 }} value={newKey} onChange={e => setNewKey(e.target.value)} placeholder="key" />
        <input style={{ ...inputStyle, flex: 1 }} value={newVal} onChange={e => setNewVal(e.target.value)} placeholder="value" />
        <button type="submit" className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px', whiteSpace: 'nowrap' }}>+ Add</button>
      </form>
    </div>
  )
}

function isOffline(device: Device): boolean {
  if (!device.last_seen || device.status !== 'active') return false
  return Date.now() - new Date(device.last_seen).getTime() > 10 * 60 * 1000
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`badge badge-${status}`}>{status}</span>
}

// ── OTA Assign Panel ─────────────────────────────────────────────────────────

function OTAAssignPanel({ tenantId, deviceId }: { tenantId: string; deviceId: string }) {
  const { data: pkgs } = usePackages()
  const assignOTA = useAssignDeviceOTA(tenantId)
  const [selectedPkgId, setSelectedPkgId] = useState('')
  const [done, setDone] = useState(false)

  const readyPkgs = (pkgs?.data ?? []).filter((p: Package) => !!p.hawkbit_distribution_set_id)

  async function handleAssign() {
    if (!selectedPkgId) return
    await assignOTA.mutateAsync({ deviceId, packageId: selectedPkgId })
    setDone(true)
    setSelectedPkgId('')
    setTimeout(() => setDone(false), 3000)
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12 }}>
        Assign a software package directly to this device. The device agent will pick it up on the next HawkBit polling cycle.
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <select
          style={{ ...inputStyle, maxWidth: 320 }}
          value={selectedPkgId}
          onChange={e => setSelectedPkgId(e.target.value)}
        >
          <option value="">— Select package —</option>
          {readyPkgs.map((p: Package) => (
            <option key={p.id} value={p.id}>{p.name} {p.version}</option>
          ))}
        </select>
        <button
          className="btn btn-primary"
          style={{ whiteSpace: 'nowrap' }}
          disabled={!selectedPkgId || assignOTA.isPending}
          onClick={handleAssign}
        >
          {assignOTA.isPending ? 'Assigning…' : 'Assign to device'}
        </button>
      </div>
      {done && <div style={{ color: '#22c55e', fontSize: 12, marginTop: 8 }}>✓ Assignment sent to HawkBit — device will update on next polling cycle.</div>}
      {assignOTA.isError && <div style={{ color: 'var(--color-error)', fontSize: 12, marginTop: 8 }}>Assignment failed. Check device backends are configured.</div>}
      {readyPkgs.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 8 }}>No packages ready in HawkBit yet. Upload an artifact in the OTA catalog first.</div>}
    </div>
  )
}

// ── Inline edit panel ────────────────────────────────────────────────────────

function EditPanel({
  device, tenantId, onClose,
  fleets, deviceTypes, playbooks,
}: {
  device: Device
  tenantId: string
  onClose: () => void
  fleets: Array<{ id: string; name: string }>
  deviceTypes: Array<{ id: string; name: string }>
  playbooks: Array<{ name: string }>
}) {
  const updateDevice = useUpdateDevice(tenantId, device.id)
  const loc = (device.location ?? {}) as Record<string, string>

  const [name, setName] = useState(device.name)
  const [fleetId, setFleetId] = useState(device.fleet_id ?? '')
  const [deviceTypeId, setDeviceTypeId] = useState(device.device_type_id ?? '')
  const [ansibleHost, setAnsibleHost] = useState((device.tags as Record<string, string>)?.ansible_host ?? '')
  const [ansibleUser, setAnsibleUser] = useState((device.tags as Record<string, string>)?.ansible_user ?? '')
  const [playbook, setPlaybook] = useState(device.bootstrap_playbook ?? '')
  const [locLat, setLocLat] = useState(loc.lat ?? '')
  const [locLon, setLocLon] = useState(loc.lon ?? '')
  const [locCountry, setLocCountry] = useState(loc.country ?? '')
  const [locCity, setLocCity] = useState(loc.city ?? '')
  const [locSite, setLocSite] = useState(loc.site ?? '')
  const [geoLoading, setGeoLoading] = useState(false)
  const [geoError, setGeoError] = useState('')
  const [customTags, setCustomTags] = useState<Record<string, unknown>>(
    Object.fromEntries(
      Object.entries(device.tags ?? {}).filter(([k]) => !RESERVED_TAG_KEYS.has(k))
    )
  )

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const tags: Record<string, unknown> = { ...customTags }
    if (ansibleHost.trim()) tags['ansible_host'] = ansibleHost.trim()
    if (ansibleUser.trim()) tags['ansible_user'] = ansibleUser.trim()

    const location: Record<string, string> = {}
    if (locLat.trim()) location.lat = locLat.trim()
    if (locLon.trim()) location.lon = locLon.trim()
    if (locCountry.trim()) location.country = locCountry.trim()
    if (locCity.trim()) location.city = locCity.trim()
    if (locSite.trim()) location.site = locSite.trim()

    await updateDevice.mutateAsync({
      name: name.trim() || undefined,
      fleet_id: fleetId || undefined,
      device_type_id: deviceTypeId || undefined,
      tags,
      location: Object.keys(location).length > 0 ? location : undefined,
      bootstrap_playbook: playbook || undefined,
    })
    onClose()
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Name</label>
          <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Fleet</label>
          <select style={{ ...inputStyle, cursor: 'pointer' }} value={fleetId} onChange={e => setFleetId(e.target.value)}>
            <option value="">— No fleet —</option>
            {fleets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Device type</label>
          <select style={{ ...inputStyle, cursor: 'pointer' }} value={deviceTypeId} onChange={e => setDeviceTypeId(e.target.value)}>
            <option value="">— Unknown —</option>
            {deviceTypes.map(dt => <option key={dt.id} value={dt.id}>{dt.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Bootstrap playbook</label>
          <select style={{ ...inputStyle, cursor: 'pointer' }} value={playbook} onChange={e => setPlaybook(e.target.value)}>
            <option value="">— Fleet / default —</option>
            {playbooks.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>IP / hostname (SSH)</label>
          <input style={inputStyle} value={ansibleHost} onChange={e => setAnsibleHost(e.target.value)} placeholder="192.168.1.10" />
        </div>
        <div>
          <label style={labelStyle}>SSH user</label>
          <input style={inputStyle} value={ansibleUser} onChange={e => setAnsibleUser(e.target.value)} placeholder="root" />
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600 }}>Location</div>
          <button type="button" className="btn btn-ghost" onClick={() => {
            if (!navigator.geolocation) { setGeoError('Geolocation not supported'); return }
            setGeoLoading(true); setGeoError('')
            navigator.geolocation.getCurrentPosition(
              pos => { setLocLat(pos.coords.latitude.toFixed(6)); setLocLon(pos.coords.longitude.toFixed(6)); setGeoLoading(false) },
              err => { setGeoError(err.message); setGeoLoading(false) },
              { enableHighAccuracy: true, timeout: 10000 }
            )
          }} disabled={geoLoading} style={{ fontSize: 11, padding: '3px 8px' }}>
            {geoLoading ? '…' : '📍 Use my location'}
          </button>
        </div>
        {geoError && <div style={{ fontSize: 11, color: 'var(--color-error)', marginBottom: 6 }}>{geoError}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Latitude</label>
            <input style={inputStyle} value={locLat} onChange={e => setLocLat(e.target.value)} placeholder="45.46" />
          </div>
          <div>
            <label style={labelStyle}>Longitude</label>
            <input style={inputStyle} value={locLon} onChange={e => setLocLon(e.target.value)} placeholder="9.19" />
          </div>
          <div>
            <label style={labelStyle}>Country</label>
            <input style={inputStyle} value={locCountry} onChange={e => setLocCountry(e.target.value)} placeholder="IT" />
          </div>
          <div>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} value={locCity} onChange={e => setLocCity(e.target.value)} placeholder="Milan" />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Site</label>
            <input style={inputStyle} value={locSite} onChange={e => setLocSite(e.target.value)} placeholder="Building A" />
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
        <TagEditor tags={customTags} onChange={setCustomTags} />
      </div>

      {updateDevice.isError && (
        <div style={{ color: 'var(--color-error)', fontSize: 12 }}>Save failed.</div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={updateDevice.isPending}>
          {updateDevice.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  )
}

// ── Tab definitions ──────────────────────────────────────────────────────────

type Tab = 'overview' | 'metrics' | 'updates' | 'playbook' | 'log'

// ── Helper: format relative time ─────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const secs = Math.round(diff / 1000)
  if (secs < 60) return `${secs}s fa`
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m fa`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h fa`
  return `${Math.round(hrs / 24)}g fa`
}

// ── Delta badge ───────────────────────────────────────────────────────────────

function DeltaBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 12,
      background: 'rgba(251,191,36,0.12)',
      border: '1px solid rgba(251,191,36,0.35)',
      color: '#fbbf24', fontSize: 11, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fbbf24', display: 'inline-block' }} />
      {count} {count === 1 ? 'modifica in attesa' : 'modifiche in attesa'}
    </span>
  )
}

// ── Stato attuale (reported) panel ────────────────────────────────────────────

/** Extract measurements from Kanto Metrics feature (Ditto protocol).
 *  Path: features.Metrics.outbox.messages[0].data.snapshot → [{id, value}]
 */
function extractKantoMetrics(features: unknown): Record<string, number> {
  try {
    const f = features as Record<string, unknown>
    const metrics = f?.['Metrics'] as Record<string, unknown> | undefined
    const msgs = (metrics?.outbox as Record<string, unknown> | undefined)?.messages
    if (!Array.isArray(msgs) || msgs.length === 0) return {}
    const snapshot = (msgs[0] as Record<string, unknown>)?.data as Record<string, unknown> | undefined
    const items = snapshot?.snapshot
    if (!Array.isArray(items)) return {}
    const out: Record<string, number> = {}
    for (const item of items as Array<{ id?: string; value?: unknown }>) {
      if (typeof item.id === 'string' && typeof item.value === 'number') {
        out[item.id] = item.value
      }
    }
    return out
  } catch {
    return {}
  }
}

function StatoAttualePanel({ reported }: { reported: Record<string, unknown> }) {
  const rows: Array<{ label: string; value: string }> = []
  const r = reported as Record<string, unknown>

  if (r.firmware) rows.push({ label: 'Firmware', value: String((r.firmware as Record<string, unknown>).version ?? r.firmware) })
  if (r.os) rows.push({ label: 'OS', value: String((r.os as Record<string, unknown>).distro ?? '') + ' ' + String((r.os as Record<string, unknown>).version ?? '') })
  if (r.kanto_version) rows.push({ label: 'Kanto', value: String(r.kanto_version) })
  if (r.log_level) rows.push({ label: 'Log level', value: String(r.log_level) })
  if (r.telemetry_interval) rows.push({ label: 'Telemetry interval', value: String(r.telemetry_interval) })
  if (r.cpu_usage != null) rows.push({ label: 'CPU', value: `${r.cpu_usage}%` })
  if (r.ram_usage != null) rows.push({ label: 'RAM', value: `${r.ram_usage}%` })
  if (r.disk_usage != null) rows.push({ label: 'Disco', value: `${r.disk_usage}%` })
  if (r.temperature != null) rows.push({ label: 'Temperatura', value: `${r.temperature}°C` })

  // Extract Kanto Metrics feature (suite-connector Ditto protocol)
  const kantoMetrics = extractKantoMetrics(r.features)
  const cpu = kantoMetrics['cpu.utilization'] ?? kantoMetrics['cpu_usage']
  const mem = kantoMetrics['memory.utilization'] ?? kantoMetrics['ram_usage']
  if (cpu != null && r.cpu_usage == null) rows.push({ label: 'CPU', value: `${(cpu * (cpu <= 1 ? 100 : 1)).toFixed(1)}%` })
  if (mem != null && r.ram_usage == null) rows.push({ label: 'RAM', value: `${(mem * (mem <= 1 ? 100 : 1)).toFixed(1)}%` })
  // Any other Kanto measurements not already shown
  const knownMetricIds = new Set(['cpu.utilization', 'cpu_usage', 'memory.utilization', 'ram_usage'])
  for (const [id, val] of Object.entries(kantoMetrics)) {
    if (!knownMetricIds.has(id)) {
      rows.push({ label: id, value: `${(val * (val <= 1 ? 100 : 1)).toFixed(2)}` })
    }
  }

  // Fallback: show remaining keys not already shown (complex objects as formatted JSON)
  const shown = new Set(['firmware', 'os', 'kanto_version', 'log_level', 'telemetry_interval',
    'cpu_usage', 'ram_usage', 'disk_usage', 'temperature', 'features'])
  const extraObjects: Array<{ label: string; value: unknown }> = []
  for (const [k, v] of Object.entries(r)) {
    if (shown.has(k)) continue
    if (typeof v === 'object' && v !== null) {
      extraObjects.push({ label: k, value: v })
    } else {
      rows.push({ label: k, value: String(v) })
    }
  }

  if (rows.length === 0 && extraObjects.length === 0) {
    return <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Nessun dato disponibile.</div>
  }

  return (
    <div>
      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 8, marginBottom: extraObjects.length > 0 ? 12 : 0 }}>
          {rows.map(({ label, value }) => (
            <React.Fragment key={label}>
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{label}</span>
              <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{value}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      {extraObjects.map(({ label, value }) => (
        <div key={label} style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
          <pre style={{
            margin: 0,
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            background: 'var(--color-surface-2)',
            border: '1px solid var(--color-border)',
            borderRadius: 4,
            padding: '8px 10px',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            color: 'var(--color-text)',
            maxHeight: 200,
          }}>{JSON.stringify(value, null, 2)}</pre>
        </div>
      ))}
    </div>
  )
}

// ── Configurazione (desired) panel ────────────────────────────────────────────

const CONFIG_KEYS = [
  { key: 'log_level', label: 'Log level', type: 'select', options: ['debug', 'info', 'warn', 'error'] },
  { key: 'telemetry_interval', label: 'Telemetry interval', type: 'text', placeholder: '30s' },
] as const

type ConfigKey = typeof CONFIG_KEYS[number]['key']

function ConfigurazionePanel({
  reported, desired, delta, tenantId, deviceId,
}: {
  reported: Record<string, unknown>
  desired: Record<string, unknown>
  delta: Record<string, unknown>
  tenantId: string
  deviceId: string
}) {
  const patchDesired = usePatchDesired(tenantId, deviceId)
  const [values, setValues] = useState<Record<ConfigKey, string>>(() => {
    const out: Record<string, string> = {}
    for (const { key } of CONFIG_KEYS) {
      out[key] = String(desired[key] ?? reported[key] ?? '')
    }
    return out as Record<ConfigKey, string>
  })
  const [dirty, setDirty] = useState(false)

  function handleChange(key: ConfigKey, val: string) {
    setValues(v => ({ ...v, [key]: val }))
    setDirty(true)
  }

  async function handlePatch(e: React.FormEvent) {
    e.preventDefault()
    const patch: Record<string, unknown> = {}
    for (const { key } of CONFIG_KEYS) {
      if (values[key]) patch[key] = values[key]
    }
    await patchDesired.mutateAsync(patch)
    setDirty(false)
  }

  function handleCancel() {
    const reset: Record<string, string> = {}
    for (const { key } of CONFIG_KEYS) {
      reset[key] = String(desired[key] ?? reported[key] ?? '')
    }
    setValues(reset as Record<ConfigKey, string>)
    setDirty(false)
  }

  const pendingCount = Object.keys(delta).length

  return (
    <div>
      {pendingCount > 0 && (
        <div style={{
          marginBottom: 14, padding: '10px 14px',
          background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)',
          borderRadius: 'var(--radius-sm)',
        }}>
          <div style={{ fontSize: 12, color: '#fbbf24', fontWeight: 600, marginBottom: 6 }}>Modifiche in attesa di applicazione</div>
          {Object.entries(delta).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 10, fontSize: 12, alignItems: 'center', marginTop: 4 }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-muted)', minWidth: 130 }}>{k}</span>
              <span style={{ fontFamily: 'var(--font-mono)', textDecoration: 'line-through', color: 'var(--color-text-dim)', fontSize: 11 }}>
                {String(reported[k] ?? '—')}
              </span>
              <span style={{ color: '#94a3b8' }}>→</span>
              <span style={{ fontFamily: 'var(--font-mono)', color: '#fbbf24' }}>{String(v)}</span>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handlePatch} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {CONFIG_KEYS.map(field => (
          <div key={field.key}>
            <label style={labelStyle}>{field.label}</label>
            {field.type === 'select' ? (
              <select
                style={{ ...inputStyle, cursor: 'pointer' }}
                value={values[field.key]}
                onChange={e => handleChange(field.key, e.target.value)}
              >
                {field.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                style={inputStyle}
                value={values[field.key]}
                onChange={e => handleChange(field.key, e.target.value)}
                placeholder={field.placeholder}
              />
            )}
          </div>
        ))}
        {dirty && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn btn-ghost" onClick={handleCancel}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={patchDesired.isPending}>
              {patchDesired.isPending ? 'Invio…' : 'Applica configurazione'}
            </button>
          </div>
        )}
        {patchDesired.isError && (
          <div style={{ color: 'var(--color-error)', fontSize: 12 }}>Errore nell'invio della configurazione.</div>
        )}
      </form>
    </div>
  )
}

// ── Metrics tab ──────────────────────────────────────────────────────────────

function MetricsTab({ tenantId, deviceId }: { tenantId: string | null; deviceId: string }) {
  const { data: dashboards, isLoading } = useDashboards(tenantId)
  const isAdmin = useRoles().some(r => r === 'tenant-admin' || r === 'platform-admin')
  const [editPanel, setEditPanel] = useState<Panel | null>(null)
  const [addingToDash, setAddingToDash] = useState<string | null>(null)

  const systemDash = dashboards?.find(d => d.kind === 'system' && d.scope === 'device')

  // Fetch full dashboard with panels
  const { data: dash } = useDashboard(tenantId, systemDash?.id ?? null)

  const deletePanel = useDeletePanel(tenantId ?? '', dash?.id ?? '')
  const [timeFrom] = useState('now-6h')
  const [timeTo]   = useState('now')

  if (isLoading) {
    return <div style={{ color: 'var(--color-text-muted)', fontSize: 13, padding: '20px 0' }}>Caricamento…</div>
  }

  if (!dash) {
    return (
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>Metriche</h3>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
          Dashboard di sistema non ancora disponibile.
          Attendi il completamento dell&apos;onboarding oppure verifica che{' '}
          <code>TELEMETRY_SERVICE_URL</code> sia impostato nel provisioning-service.
        </div>
      </div>
    )
  }

  const panels = [...(dash.panels ?? [])].sort((a, b) => a.position - b.position)

  return (
    <div>
      {/* ── Admin toolbar ─────────────────────────────────────── */}
      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12 }}
            onClick={() => setAddingToDash(dash.id)}
          >
            + Aggiungi panel
          </button>
        </div>
      )}

      {/* ── Panel grid ────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {panels.map(panel => (
          <MetricsPanel
            key={panel.id}
            panel={panel}
            tenantId={tenantId!}
            variables={{ device_id: deviceId }}
            timeFrom={timeFrom}
            timeTo={timeTo}
            height={200}
            onEdit={isAdmin ? () => setEditPanel(panel) : undefined}
            onDelete={isAdmin ? () => {
              if (confirm(`Eliminare il panel "${panel.title}"?`)) {
                deletePanel.mutate(panel.id)
              }
            } : undefined}
          />
        ))}
      </div>

      {/* ── Panel editor modal ─────────────────────────────────── */}
      {editPanel && (
        <PanelEditorModal
          tenantId={tenantId!}
          dashboardId={dash.id}
          panel={editPanel}
          onClose={() => setEditPanel(null)}
        />
      )}
      {addingToDash && (
        <PanelEditorModal
          tenantId={tenantId!}
          dashboardId={addingToDash}
          onClose={() => setAddingToDash(null)}
        />
      )}
    </div>
  )
}

// ── Panel editor modal ────────────────────────────────────────────────────────

interface PanelEditorModalProps {
  tenantId: string
  dashboardId: string
  panel?: Panel
  onClose: () => void
}

function PanelEditorModal({ tenantId, dashboardId, panel, onClose }: PanelEditorModalProps) {
  const createPanel = useCreatePanel(tenantId, dashboardId)
  const updatePanel = useUpdatePanel(tenantId, dashboardId)

  const [title,     setTitle]     = useState(panel?.title ?? '')
  const [query,     setQuery]     = useState(panel?.query ?? '')
  const [queryType, setQueryType] = useState<'promql' | 'logql'>(panel?.query_type ?? 'promql')
  const [panelType, setPanelType] = useState<PanelType>(panel?.panel_type ?? 'timeseries')
  const [unit,      setUnit]      = useState(panel?.unit ?? 'short')

  async function handleSave() {
    if (!title.trim() || !query.trim()) return
    if (panel) {
      await updatePanel.mutateAsync({ panelId: panel.id, req: { title, query, query_type: queryType, panel_type: panelType, unit } })
    } else {
      await createPanel.mutateAsync({ title, query, query_type: queryType, panel_type: panelType, unit })
    }
    onClose()
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius)',
        padding: 24,
        width: 520,
        maxHeight: '80vh',
        overflowY: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>
          {panel ? 'Modifica panel' : 'Nuovo panel'}
        </h3>

        <label style={editorLabelStyle}>Titolo</label>
        <input style={editorInputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="CPU Usage" />

        <label style={editorLabelStyle}>Tipo query</label>
        <select style={editorInputStyle} value={queryType} onChange={e => setQueryType(e.target.value as 'promql' | 'logql')}>
          <option value="promql">PromQL</option>
          <option value="logql">LogQL</option>
        </select>

        <label style={editorLabelStyle}>Query</label>
        <textarea
          style={{ ...editorInputStyle, height: 80, fontFamily: 'var(--font-mono)', fontSize: 12, resize: 'vertical' }}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={queryType === 'promql' ? 'cpu_usage_active{device_id="$device_id"}' : '{device_id="$device_id"} | json'}
        />

        <label style={editorLabelStyle}>Tipo visualizzazione</label>
        <select style={editorInputStyle} value={panelType} onChange={e => setPanelType(e.target.value as PanelType)}>
          <option value="timeseries">Time series</option>
          <option value="stat">Stat (ultimo valore)</option>
          <option value="gauge">Gauge</option>
          {queryType === 'logql' && <option value="logs">Log stream</option>}
        </select>

        <label style={editorLabelStyle}>Unità</label>
        <select style={editorInputStyle} value={unit} onChange={e => setUnit(e.target.value)}>
          <option value="short">Numero</option>
          <option value="percent">Percentuale (%)</option>
          <option value="bytes">Byte</option>
          <option value="Bps">Byte/s (network)</option>
          <option value="s">Secondi</option>
          <option value="none">Nessuna</option>
        </select>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={createPanel.isPending || updatePanel.isPending}
          >
            {panel ? 'Salva' : 'Crea'}
          </button>
        </div>
      </div>
    </div>
  )
}

const editorLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 4,
  marginTop: 12,
}

const editorInputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  padding: '8px 10px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
}

// ── Main page ────────────────────────────────────────────────────────────────

export function DeviceDetail({ tenantId }: Props) {
  const { deviceId } = useParams<{ deviceId: string }>()
  const navigate = useNavigate()
  const roles = useRoles()
  const isPlatformAdmin = roles.includes('platform-admin')

  const { data: device, isLoading } = useDevice(tenantId, deviceId ?? null)
  const { data: fleetsResult } = useFleets(tenantId)
  const { data: deviceTypesResult } = useDeviceTypes(tenantId)
  const { data: playbooksResult } = usePlaybooks(tenantId)
  const { data: backends } = useDeviceBackends(tenantId, deviceId ?? null)
  const { data: jobs } = useDeviceJobs(tenantId, deviceId ?? null)
  const { data: sessions } = useSSHSessions(tenantId, deviceId ?? null)
  const { data: twin } = useTwin(tenantId, deviceId ?? null)

  const deleteDevice = useDeleteDevice(tenantId ?? '')
  const reprovision = useReprovisionDevice(tenantId ?? '')
  const setStatus = useSetDeviceStatus(tenantId ?? '')

  const [tab, setTab] = useState<Tab>('overview')
  const [sshOpen, setSSHOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null)

  if (!tenantId) return <div style={{ padding: 32, color: 'var(--color-text-muted)' }}>Seleziona prima un tenant.</div>
  if (isLoading) return <div style={{ padding: 32, color: 'var(--color-text-muted)' }}>Caricamento…</div>
  if (!device) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ color: 'var(--color-error)' }}>Device non trovato.</div>
        <Link to="/devices" style={{ color: 'var(--color-accent)', fontSize: 13, marginTop: 8, display: 'block' }}>← Torna ai device</Link>
      </div>
    )
  }

  const fleets = fleetsResult?.data ?? []
  const deviceTypes = deviceTypesResult?.data ?? []
  const playbooks = playbooksResult ?? []
  const fleetName = fleets.find(f => f.id === device.fleet_id)?.name
  const deviceTypeName = deviceTypes.find(dt => dt.id === device.device_type_id)?.name
  const loc = (device.location ?? {}) as Record<string, string>
  const offline = isOffline(device)

  const reported = twin?.reported?.state ?? {}
  const desired = twin?.desired?.state ?? {}
  const delta = twin?.delta ?? {}
  const pendingCount = Object.keys(delta).length

  async function handleDelete() {
    await deleteDevice.mutateAsync(device!.id)
    navigate('/devices')
  }

  async function handleReprovision() {
    await reprovision.mutateAsync(device!.id)
    // Switch to overview and auto-expand the newest job (which will appear after
    // the device-jobs invalidation triggered by useReprovisionDevice.onSuccess).
    setTab('overview')
    setExpandedJobId('__latest__')
  }

  async function handleMaintenance() {
    const isMaintenance = device!.status === 'maintenance'
    await setStatus.mutateAsync({ deviceId: device!.id, status: isMaintenance ? 'active' : 'maintenance' })
  }

  const TAB_STYLE = (active: boolean): React.CSSProperties => ({
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    color: active ? 'var(--color-text-bright)' : 'var(--color-text-muted)',
    background: 'none',
    border: 'none',
    borderBottom: active ? '2px solid var(--color-accent)' : '2px solid transparent',
    cursor: 'pointer',
    transition: 'color 0.15s',
    marginBottom: -1,
  })

  return (
    <div style={{ padding: '24px 32px', maxWidth: 960 }}>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 14 }}>
        <Link to="/devices" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>Device</Link>
        <span style={{ margin: '0 6px' }}>›</span>
        <span>{device.name}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{device.name}</h1>
            <StatusBadge status={device.status} />
            {pendingCount > 0 && <DeltaBadge count={pendingCount} />}
            {offline && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#f59e0b' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
                offline
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            {fleetName && <span>tenant: {fleetName}</span>}
            {fleetName && (loc.site || loc.city) && <span style={{ margin: '0 6px' }}>·</span>}
            {(loc.site || loc.city) && <span>gruppo: {[loc.site, loc.city].filter(Boolean).join(', ')}</span>}
            {device.last_seen && (
              <>
                <span style={{ margin: '0 6px' }}>·</span>
                <span>ultimo heartbeat: {relativeTime(device.last_seen)}</span>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <RequirePermission perm="devices:provision">
            <button className="btn btn-ghost" onClick={() => setEditing(e => !e)}>
              {editing ? 'Annulla modifica' : 'Esegui playbook'}
            </button>
          </RequirePermission>
          {device.status === 'active' && (
            <RequirePermission perm="devices:provision">
              <button className="btn btn-primary" onClick={() => setSSHOpen(true)}>Connetti SSH</button>
            </RequirePermission>
          )}
          <RequirePermission perm="devices:delete">
            {confirmDelete ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                Eliminare?&nbsp;
                <button className="btn btn-danger" style={{ padding: '4px 8px', fontSize: 11 }} onClick={handleDelete} disabled={deleteDevice.isPending}>
                  {deleteDevice.isPending ? '…' : 'Sì'}
                </button>
                <button className="btn btn-ghost" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => setConfirmDelete(false)}>No</button>
              </span>
            ) : (
              <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>Rimuovi</button>
            )}
          </RequirePermission>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid var(--color-border)', marginBottom: 20, display: 'flex', gap: 0 }}>
        {(['overview', 'metrics', 'updates', 'playbook', 'log'] as Tab[]).map(t => (
          <button key={t} style={TAB_STYLE(tab === t)} onClick={() => setTab(t)}>
            {{ overview: 'Panoramica', metrics: 'Metriche', updates: 'Aggiornamenti', playbook: 'Playbook', log: 'Log' }[t]}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div>
          {(reported.cpu_usage != null || reported.ram_usage != null || reported.disk_usage != null || reported.temperature != null) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
              {(
                [
                  { label: 'CPU', value: reported.cpu_usage, unit: '%' },
                  { label: 'RAM', value: reported.ram_usage, unit: '%' },
                  { label: 'Disco', value: reported.disk_usage, unit: '%' },
                  { label: 'Temperatura', value: reported.temperature, unit: '°C' },
                ] as Array<{ label: string; value: unknown; unit: string }>
              ).filter(m => m.value != null).map(({ label, value, unit }) => (
                <div key={label} style={{
                  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)', padding: '14px 18px',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>
                    {String(value)}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: 2 }}>{unit}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div style={{ ...sectionStyle, marginBottom: 0 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stato attuale</h3>
              {twin ? (
                <StatoAttualePanel reported={reported} />
              ) : (
                <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Nessun dato twin disponibile.</div>
              )}
            </div>

            <div style={{ ...sectionStyle, marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Configurazione</h3>
                {pendingCount > 0 && <DeltaBadge count={pendingCount} />}
              </div>
              <RequirePermission perm="devices:write">
                <ConfigurazionePanel
                  reported={reported}
                  desired={desired}
                  delta={delta}
                  tenantId={tenantId}
                  deviceId={device.id}
                />
              </RequirePermission>
            </div>
          </div>

          {!!(reported.firmware as Record<string, unknown> | undefined)?.version && (
            <div style={{ ...sectionStyle }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 600 }}>Aggiornamento firmware</h3>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    Versione installata: <strong>{String((reported.firmware as Record<string, unknown>).version ?? '')}</strong>
                    {(() => {
                      const rv = String((reported.firmware as Record<string, unknown>).version ?? '')
                      const dv = String((desired.firmware as Record<string, unknown> | undefined)?.version ?? '')
                      if (!dv || dv === rv) return null
                      return <> · Disponibile: <strong style={{ color: '#22c55e' }}>{dv}</strong></>
                    })()}
                  </div>
                </div>
                <RequirePermission perm="fleets:write">
                  <OTAAssignPanel tenantId={tenantId} deviceId={device.id} />
                </RequirePermission>
              </div>
            </div>
          )}

          {jobs && jobs.length > 0 && (
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 14px', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Attività recenti</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {jobs.slice(0, 5).map((job, idx) => {
                  // '__latest__' is a sentinel set after reprovision — auto-expand job[0].
                  const isExpanded =
                    expandedJobId === job.id ||
                    (expandedJobId === '__latest__' && idx === 0)
                  return (
                    <div key={job.id}>
                      <div
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 0', borderBottom: isExpanded ? 'none' : '1px solid var(--color-border)',
                          cursor: job.steps?.length > 0 ? 'pointer' : 'default',
                        }}
                        onClick={() => {
                          if (!job.steps?.length) return
                          setExpandedJobId(isExpanded ? null : job.id)
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                          {job.steps?.length > 0 && (
                            <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                              {isExpanded ? '▾' : '▸'}
                            </span>
                          )}
                          <span>{job.type}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                          <span className={`badge badge-${job.status === 'success' ? 'active' : job.status === 'failed' ? 'failed' : 'pending'}`}>
                            {job.status}
                          </span>
                          <span style={{ color: 'var(--color-text-muted)' }}>
                            {job.completed_at ? relativeTime(job.completed_at) : job.started_at ? relativeTime(job.started_at) : '—'}
                          </span>
                        </div>
                      </div>
                      {isExpanded && job.steps?.length > 0 && (
                        <JobStepsPanel
                          steps={job.steps}
                          jobStatus={job.status}
                          onClose={() => setExpandedJobId(null)}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div style={sectionStyle}>
            <h3 style={{ margin: '0 0 14px', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Informazioni device</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
              <div><span style={labelStyle}>Fleet</span><div style={{ fontSize: 13 }}>{fleetName ?? '—'}</div></div>
              <div><span style={labelStyle}>Tipo</span><div style={{ fontSize: 13 }}>{deviceTypeName ?? '—'}</div></div>
              <div><span style={labelStyle}>Registrato</span><div style={{ fontSize: 13 }}>{new Date(device.registered_at).toLocaleString()}</div></div>
              {device.last_seen && (
                <div><span style={labelStyle}>Ultimo heartbeat</span><div style={{ fontSize: 13 }}>{new Date(device.last_seen).toLocaleString()}</div></div>
              )}
              {loc.site && <div><span style={labelStyle}>Sede</span><div style={{ fontSize: 13 }}>{loc.site}</div></div>}
              {loc.city && <div><span style={labelStyle}>Città</span><div style={{ fontSize: 13 }}>{loc.city}</div></div>}
              {loc.country && <div><span style={labelStyle}>Paese</span><div style={{ fontSize: 13 }}>{loc.country}</div></div>}
              {(device.tags as Record<string, string> | undefined)?.ansible_host && (
                <div><span style={labelStyle}>IP SSH</span><div style={{ fontSize: 13, fontFamily: 'var(--font-mono)' }}>{(device.tags as Record<string, string>).ansible_host}</div></div>
              )}
              {isPlatformAdmin && backends?.awx_host_id && (
                <div><span style={labelStyle}>AWX host ID</span><div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-text-muted)' }}>{backends.awx_host_id}</div></div>
              )}
              {isPlatformAdmin && backends?.hawkbit_controller_id && (
                <div><span style={labelStyle}>HawkBit ID</span><div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-text-muted)' }}>{backends.hawkbit_controller_id}</div></div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <RequirePermission perm="devices:write">
                <button className="btn btn-ghost" onClick={() => setEditing(e => !e)} style={{ fontSize: 12 }}>
                  {editing ? 'Chiudi modifica' : 'Modifica device'}
                </button>
              </RequirePermission>
              {(device.status === 'active' || device.status === 'maintenance') && (
                <RequirePermission perm="devices:write">
                  <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={handleMaintenance}>
                    {device.status === 'maintenance' ? 'Ripristina attivo' : 'Metti in manutenzione'}
                  </button>
                </RequirePermission>
              )}
              {(device.status === 'failed' || device.status === 'pending') && (
                <RequirePermission perm="devices:provision">
                  <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={handleReprovision} disabled={reprovision.isPending}>
                    {reprovision.isPending ? 'Reprovisioning…' : 'Riprovision'}
                  </button>
                </RequirePermission>
              )}
            </div>
          </div>

          {editing && (
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Modifica device</h3>
              <EditPanel
                device={device}
                tenantId={tenantId}
                onClose={() => setEditing(false)}
                fleets={fleets}
                deviceTypes={deviceTypes}
                playbooks={playbooks}
              />
            </div>
          )}

          {sessions && sessions.length > 0 && (
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 14px', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sessioni SSH recenti</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 10px 8px 0', color: 'var(--color-text-muted)', fontWeight: 500 }}>Utente</th>
                    <th style={{ textAlign: 'left', padding: '4px 10px 8px 0', color: 'var(--color-text-muted)', fontWeight: 500 }}>Stato</th>
                    <th style={{ textAlign: 'left', padding: '4px 10px 8px 0', color: 'var(--color-text-muted)', fontWeight: 500 }}>Avviata</th>
                    <th style={{ textAlign: 'left', padding: '4px 0 8px 0', color: 'var(--color-text-muted)', fontWeight: 500 }}>Chiusa</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.slice(0, 5).map(s => (
                    <tr key={s.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 10px 6px 0', fontFamily: 'var(--font-mono)' }}>{s.user_name || s.user_sub}</td>
                      <td style={{ padding: '6px 10px 6px 0' }}>
                        <span style={{
                          fontSize: 11, padding: '1px 6px', borderRadius: 3, fontWeight: 600,
                          background: s.state === 'open' ? '#22c55e22' : 'var(--color-surface-2)',
                          color: s.state === 'open' ? '#22c55e' : 'var(--color-text-muted)',
                        }}>{s.state}</span>
                      </td>
                      <td style={{ padding: '6px 10px 6px 0', color: 'var(--color-text-muted)' }}>{new Date(s.opened_at).toLocaleString()}</td>
                      <td style={{ padding: '6px 0', color: 'var(--color-text-muted)' }}>{s.closed_at ? new Date(s.closed_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'metrics' && (
        <MetricsTab tenantId={tenantId} deviceId={device.id} />
      )}

      {tab === 'updates' && (
        <div>
          <div style={sectionStyle}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Firmware OTA</h3>
            <RequirePermission perm="fleets:write">
              <OTAAssignPanel tenantId={tenantId} deviceId={device.id} />
            </RequirePermission>
          </div>
          {jobs && jobs.length > 0 && (
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600 }}>Storico aggiornamenti</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Tipo', 'Stato', 'Avviato', 'Completato'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '4px 10px 8px 0', color: 'var(--color-text-muted)', fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <tr key={job.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 10px 6px 0', fontFamily: 'var(--font-mono)' }}>{job.type}</td>
                      <td style={{ padding: '6px 10px 6px 0' }}>
                        <span className={`badge badge-${job.status === 'success' ? 'active' : job.status === 'failed' ? 'failed' : 'pending'}`}>{job.status}</span>
                      </td>
                      <td style={{ padding: '6px 10px 6px 0', color: 'var(--color-text-muted)' }}>{job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</td>
                      <td style={{ padding: '6px 0', color: 'var(--color-text-muted)' }}>{job.completed_at ? new Date(job.completed_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'playbook' && (
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Esegui playbook</h3>
          <RequirePermission perm="operations:execute">
            <PlaybookRunPanel tenantId={tenantId} deviceId={device.id} playbooks={playbooks} />
          </RequirePermission>
        </div>
      )}

      {tab === 'log' && (
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Log</h3>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Log streaming non ancora disponibile in questo rilascio.</div>
        </div>
      )}

      {sshOpen && tenantId && (
        <WebSSHModal
          tenantID={tenantId}
          deviceID={device.id}
          deviceName={device.name}
          onClose={() => setSSHOpen(false)}
        />
      )}
    </div>
  )
}

// ── Job steps panel ───────────────────────────────────────────────────────────

function JobStepsPanel({
  steps,
  jobStatus,
  onClose,
}: {
  steps: Array<{ step: string; status: string; error?: string; at: string }>
  jobStatus: string
  onClose: () => void
}) {
  const isRunning = jobStatus === 'running' || jobStatus === 'pending'

  return (
    <div style={{
      background: 'var(--color-surface-2)',
      border: '1px solid var(--color-border)',
      borderTop: 'none',
      borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
      marginBottom: 1,
      padding: '10px 14px 12px',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {steps.map((s, i) => {
          const icon =
            s.status === 'success' ? { ch: '✓', col: 'var(--color-success)' } :
            s.status === 'failed'  ? { ch: '✗', col: 'var(--color-danger)' } :
            s.status === 'running' ? { ch: '↻', col: 'var(--color-primary)' } :
                                     { ch: '·', col: 'var(--color-text-muted)' }
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12 }}>
              <span style={{
                minWidth: 14, fontWeight: 700, color: icon.col,
                animation: s.status === 'running' ? 'spin 1s linear infinite' : undefined,
              }}>
                {icon.ch}
              </span>
              <div style={{ flex: 1 }}>
                <span style={{ fontFamily: 'var(--font-mono)', color: s.status === 'failed' ? 'var(--color-danger)' : 'var(--color-text)' }}>
                  {s.step}
                </span>
                {s.error && (
                  <div style={{
                    marginTop: 3, padding: '4px 8px',
                    background: '#ef444420', borderRadius: 3,
                    color: 'var(--color-danger)', fontFamily: 'var(--font-mono)', fontSize: 11,
                  }}>
                    {s.error}
                  </div>
                )}
              </div>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 11, whiteSpace: 'nowrap' }}>
                {new Date(s.at).toLocaleTimeString()}
              </span>
            </div>
          )
        })}
        {isRunning && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-muted)', paddingTop: 4 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            In esecuzione…
          </div>
        )}
      </div>
      <button
        onClick={onClose}
        style={{ marginTop: 10, fontSize: 11, color: 'var(--color-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        Chiudi ▴
      </button>
    </div>
  )
}

// ── Playbook run panel ────────────────────────────────────────────────────────

function PlaybookRunPanel({ tenantId, deviceId, playbooks }: { tenantId: string; deviceId: string; playbooks: Array<{ name: string }> }) {
  const runPlaybook = useRunPlaybook(tenantId)
  const [selected, setSelected] = useState('')
  const [done, setDone] = useState(false)

  async function handleRun() {
    if (!selected) return
    await runPlaybook.mutateAsync({ deviceId, playbook: selected })
    setDone(true)
    setTimeout(() => setDone(false), 3000)
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <select style={{ ...inputStyle, maxWidth: 320 }} value={selected} onChange={e => setSelected(e.target.value)}>
          <option value="">— Seleziona playbook —</option>
          {playbooks.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
        <button className="btn btn-primary" disabled={!selected || runPlaybook.isPending} onClick={handleRun}>
          {runPlaybook.isPending ? 'Avvio…' : 'Esegui'}
        </button>
      </div>
      {done && <div style={{ color: '#22c55e', fontSize: 12 }}>✓ Playbook avviato — monitora lo stato nella tab Aggiornamenti.</div>}
      {runPlaybook.isError && <div style={{ color: 'var(--color-error)', fontSize: 12 }}>Errore nell'avvio del playbook.</div>}
    </div>
  )
}
